/**
 * Accessible Navigation – Disclosure pattern (WAI-ARIA APG)
 *
 * Each dropdown trigger is a dedicated toggle button
 * <button aria-expanded="false" aria-controls="ID"> placed next to a top-level link.
 * The controlled element is a <ul id="ID"> containing <li><a> links.
 *
 * Keyboard behaviour (per WCAG 2.1 SC 2.1.1):
 *   Trigger button:
 *     Space / Enter  – toggle dropdown
 *     Arrow Down     – open dropdown, move focus to first item
 *     Arrow Up       – open dropdown, move focus to last item
 *     Escape         – close dropdown (if open)
 *
 *   Inside dropdown:
 *     Arrow Down     – move to next item (wraps to first)
 *     Arrow Up       – move to previous item (wraps to last)
 *     Home           – move to first item
 *     End            – move to last item
 *     Escape         – close dropdown, return focus to trigger
 *     Tab            – close dropdown, move focus naturally
 *
 *   Pointer / mouse:
 *     Click outside  – close all open dropdowns
 *
 * @module navigation
 */

const navigationRegistry = [];
let hasNavigationDocumentClickListener = false;
const desktopMediaQuery = window.matchMedia('(min-width: 1280px)');

/**
 * Returns the submenu element controlled by a trigger button.
 * Resolves via aria-controls ID first, then falls back to the next sibling <ul>
 * after the closest navigation item head wrapper.
 *
 * @param {HTMLButtonElement} btn - Dropdown trigger button
 * @returns {HTMLElement|null}
 */
function getSubmenu(btn) {
  const id = btn.getAttribute('aria-controls');
  if (id) {
    return document.getElementById(id);
  }

  const itemHead = btn.closest('.navigation__item-head');
  if (itemHead) {
    const sibling = itemHead.nextElementSibling;
    return sibling && sibling.tagName === 'UL' ? sibling : null;
  }

  const sibling = btn.nextElementSibling;
  return sibling && sibling.tagName === 'UL' ? sibling : null;
}

/**
 * Returns all focusable link/button items within a submenu.
 *
 * @param {HTMLElement} submenu - The dropdown <ul> element to query
 * @returns {HTMLElement[]}
 */
function getSubmenuItems(submenu) {
  return Array.from(submenu.querySelectorAll('a, button'));
}

/**
 * Opens a single dropdown and moves focus to the target item.
 *
 * @param {HTMLButtonElement} btn          - Trigger button
 * @param {HTMLElement}       submenu      - The <ul> to open
 * @param {'first'|'last'}    [focusTarget='first'] - Which item to focus
 * @returns {void}
 */
function openDropdown(btn, submenu, focusTarget = 'first') {
  btn.setAttribute('aria-expanded', 'true');
  submenu.classList.add('is-open');

  const items = getSubmenuItems(submenu);
  if (!items.length) return;

  const target = focusTarget === 'last' ? items[items.length - 1] : items[0];
  target.focus();
}

/**
 * Closes a single dropdown and optionally returns focus to the trigger.
 *
 * @param {HTMLButtonElement} btn              - Trigger button
 * @param {HTMLElement}       submenu          - The <ul> to close
 * @param {boolean}           [returnFocus=false] - When true, moves focus back to the trigger button
 * @returns {void}
 */
function closeDropdown(btn, submenu, returnFocus = false) {
  btn.setAttribute('aria-expanded', 'false');
  submenu.classList.remove('is-open');

  if (returnFocus) {
    btn.focus();
  }
}

/**
 * Closes all open dropdowns within a navigation element.
 *
 * @param {HTMLElement} nav                    - The .navigation root
 * @param {boolean}     [returnFocus=false]    - When true, moves focus back to each trigger button
 * @returns {void}
 */
function closeAllDropdowns(nav, returnFocus = false) {
  nav.querySelectorAll('button[aria-expanded="true"]').forEach((btn) => {
    const submenu = getSubmenu(btn);
    if (submenu) {
      closeDropdown(btn, submenu, returnFocus);
    }
  });
}

/**
 * Attaches keyboard handlers inside an open submenu.
 *
 * @param {HTMLButtonElement} btn     - Trigger button (owner of the submenu)
 * @param {HTMLElement}       submenu - The open <ul>
 * @param {KeyboardEvent}     e       - The keydown event to handle
 * @returns {void}
 */
function handleSubmenuKeydown(btn, submenu, e) {
  const items = getSubmenuItems(submenu);
  const currentIndex = items.indexOf(document.activeElement);

  switch (e.key) {
  case 'ArrowDown': {
    e.preventDefault();
    const next = (currentIndex + 1) % items.length;
    items[next].focus();
    break;
  }
  case 'ArrowUp': {
    e.preventDefault();
    const prev = (currentIndex - 1 + items.length) % items.length;
    items[prev].focus();
    break;
  }
  case 'Home':
    e.preventDefault();
    items[0].focus();
    break;
  case 'End':
    e.preventDefault();
    items[items.length - 1].focus();
    break;
  case 'Escape':
    e.preventDefault();
    closeDropdown(btn, submenu, true);
    break;
  case 'Tab':
    // Let Tab move focus naturally; close the dropdown without stealing focus.
    closeDropdown(btn, submenu, false);
    break;
  default:
    break;
  }
}

/**
 * Initialises accessible navigation for all `.navigation` elements in the
 * document. Safe to call after DOMContentLoaded.
 *
 * @returns {void}
 */
function initNavigation() {
  const navElements = document.querySelectorAll('.navigation');
  if (!navElements.length) return;

  navElements.forEach((nav) => {
    if (nav.dataset.navigationInitialized === 'true') return;

    const triggers = nav.querySelectorAll('.navigation__toggle[aria-expanded], button[aria-expanded]');
    nav.dataset.navigationInitialized = 'true';
    navigationRegistry.push(nav);

    triggers.forEach((btn) => {
      const submenu = getSubmenu(btn);
      if (!submenu) return;
      const item = btn.closest('li');

      // ── Trigger button ──────────────────────────────────────────────────
      btn.addEventListener('click', () => {
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';

        // Close every other open dropdown first
        nav.querySelectorAll('button[aria-expanded="true"]').forEach((other) => {
          if (other !== btn) {
            const otherSubmenu = getSubmenu(other);
            if (otherSubmenu) closeDropdown(other, otherSubmenu, false);
          }
        });

        if (isExpanded) {
          closeDropdown(btn, submenu, false);
        } else {
          openDropdown(btn, submenu, 'first');
        }
      });

      btn.addEventListener('keydown', (e) => {
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';

        switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isExpanded) {
            closeAllDropdowns(nav);
          }
          openDropdown(btn, submenu, 'first');
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!isExpanded) {
            closeAllDropdowns(nav);
          }
          openDropdown(btn, submenu, 'last');
          break;
        case 'Escape':
          if (isExpanded) {
            e.preventDefault();
            closeDropdown(btn, submenu, true);
          }
          break;
        default:
          break;
        }
      });

      // ── Submenu items ───────────────────────────────────────────────────
      submenu.addEventListener('keydown', (e) => {
        handleSubmenuKeydown(btn, submenu, e);
      });

      if (item instanceof HTMLElement) {
        item.addEventListener('mouseenter', () => {
          if (!desktopMediaQuery.matches) return;

          nav.querySelectorAll('button[aria-expanded="true"]').forEach((other) => {
            if (other !== btn) {
              const otherSubmenu = getSubmenu(other);
              if (otherSubmenu) closeDropdown(other, otherSubmenu, false);
            }
          });

          btn.setAttribute('aria-expanded', 'true');
          submenu.classList.add('is-open');
        });

        item.addEventListener('mouseleave', () => {
          if (!desktopMediaQuery.matches) return;
          closeDropdown(btn, submenu, false);
        });
      }
    });

    // ── Focus leaves the entire nav (e.g. Tab past last item) ──────────────
    nav.addEventListener('focusout', (e) => {
      if (!nav.contains(e.relatedTarget)) {
        closeAllDropdowns(nav, false);
      }
    });
  });

  if (hasNavigationDocumentClickListener) return;

  document.addEventListener('click', (e) => {
    navigationRegistry.forEach((nav) => {
      if (!nav.contains(e.target)) {
        closeAllDropdowns(nav, false);
      }
    });
  });

  hasNavigationDocumentClickListener = true;
}


/**
 * Mobile navigation overlay and hamburger logic
 *
 * - Handles opening/closing the mobile menu overlay
 * - Locks body scroll when menu is open
 * - Ensures ARIA and keyboard accessibility
 *
 * @returns {void}
 */
function initMobileNavigation() {
  const nav = document.querySelector('.navigation');
  const hamburger = document.querySelector('.nav-hamburger');
  const overlay = document.querySelector('.nav-overlay');
  if (!nav || !hamburger || !overlay) return;

  /**
   * Returns the first focusable navigation control inside the mobile menu.
   *
   * @returns {HTMLElement|null}
   */
  function getFirstNavControl() {
    return nav.querySelector('.container > li:first-child .navigation__link, .container > li:first-child .navigation__toggle, .container > li:first-child a, .container > li:first-child button');
  }

  // Open mobile menu
  function openMenu() {
    overlay.classList.add('is-open');
    nav.classList.add('is-mobile-open');
    hamburger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('nav-lock-scroll');
    // Focus first nav item
    const firstLink = getFirstNavControl();
    if (firstLink) firstLink.focus();
  }

  // Close mobile menu
  function closeMenu(restoreFocus = true) {
    overlay.classList.remove('is-open');
    nav.classList.remove('is-mobile-open');
    hamburger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('nav-lock-scroll');
    if (restoreFocus) {
      hamburger.focus();
    }
  }

  hamburger.addEventListener('click', () => {
    const expanded = hamburger.getAttribute('aria-expanded') === 'true';
    if (expanded) {
      closeMenu();
    } else {
      openMenu();
    }
  });

  // Close on overlay click or ESC
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeMenu();
  });
  document.addEventListener('keydown', (e) => {
    if (overlay.classList.contains('is-open') && e.key === 'Escape') {
      closeMenu();
    }
  });

  // Trap focus inside nav when open (basic focus trap)
  document.addEventListener('focusin', (e) => {
    if (!overlay.classList.contains('is-open')) return;
    if (!nav.contains(e.target) && e.target !== hamburger) {
      // Move focus back to nav
      const firstLink = getFirstNavControl();
      if (firstLink) firstLink.focus();
    }
  });

  // Close mobile menu automatically when the viewport crosses into desktop width.
  // This prevents a stuck open overlay if the user resizes or rotates from mobile.
  desktopMediaQuery.addEventListener('change', (e) => {
    if (e.matches && overlay.classList.contains('is-open')) {
      closeMenu(false);
    }
  });
}

/**
 * Hides the site header when scrolling down, reveals it when scrolling up.
 *
 * @returns {void}
 */
function initScrollBehavior() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  let lastScrollY = window.scrollY;
  let ticking = false;

  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;

    requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 80) {
        header.classList.add('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    });
  }, { passive: true });
}

// Init both desktop and mobile navigation
/**
 * Initialises desktop dropdown navigation and the mobile overlay menu.
 *
 * @returns {void}
 */
function initMakronNavigation() {
  initNavigation();
  initMobileNavigation();
  initScrollBehavior();
}

export { initMakronNavigation };
